import React from 'react';
import useComponentDidMount from 'Hooks/useComponentDidMount';

export const STATUS_MAP = {
  waiting: 'waiting',
  working: 'working',
  finished: 'finished',
};

const getInitialState = () => {
  return {
    status: STATUS_MAP.waiting,
    progress: 0,
  };
};

const MAX_PROGRESS = 100;
const TICK_DURATION = 100;

const INCREMENT_PROGRESS = 'increment-progress';

const RESET = 'reset-status';
const START_WORKING = 'start-working';
const FINISH_WORKING = 'finish-working';

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INCREMENT_PROGRESS: {
      return {
        ...state,
        progress: state.progress + action.progressDelta,
      };
    }
    case RESET: {
      return {
        ...state,
        status: STATUS_MAP.waiting,
        progress: 0,
      };
    }
    case START_WORKING: {
      return {
        ...state,
        status: STATUS_MAP.working,
        progress: 0,
      };
    }
    case FINISH_WORKING: {
      return {
        ...state,
        status: STATUS_MAP.finished,
      };
    }
    default: {
      throw new Error(`Unhandled SurveyContext action type: ${action.type}`);
    }
  }
};

export default function useRefreshIndicator(args = {}) {
  const {
    duration = 60000, // 100 * 600 = 60000 or 1 minute
    finishDuration = 1000,
  } = args;
  const mountedRef = React.useRef(false);
  const intervalRef = React.useRef(null);
  const progressRef = React.useRef(0);
  const [state, dispatch] = React.useReducer(reducer, null, getInitialState);

  const { progress } = state;
  const numTicks = Math.max(duration / TICK_DURATION, 1);
  const progressDelta = MAX_PROGRESS / numTicks;

  // Keep a ref up to date with where progress is
  React.useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  const handleStartWorking = () => {
    dispatch({ type: START_WORKING });

    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      dispatch({ type: INCREMENT_PROGRESS, progressDelta });
    }, TICK_DURATION);
  };

  const handleFinishWorking = () => {
    try {
      dispatch({ type: FINISH_WORKING });
      const currProgress = progressRef.current;
      const remainingProgress = MAX_PROGRESS - currProgress;
      const numTicksRemaining = Math.max((finishDuration / 2) / TICK_DURATION, 1);
      const finishProgressDelta = remainingProgress / numTicksRemaining;

      // Increment to MAX_PROGRESS over the finish duration
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        if (mountedRef.current) {
          dispatch({ type: INCREMENT_PROGRESS, progressDelta: finishProgressDelta });
        }
      }, TICK_DURATION);

      // Reset the component if it's still mounted
      if (mountedRef.current) {
        setTimeout(() => {
          clearInterval(intervalRef.current);
          dispatch({ type: RESET });
        }, finishDuration);
      }
    } catch (err) {
      clearInterval(intervalRef.current);
    }
  };

  const handleReset = () => {
    dispatch({ type: RESET });
  };

  useComponentDidMount(() => {
    mountedRef.current = true;
  }, () => {
    mountedRef.current = false;
    clearInterval(intervalRef.current);
  });

  return {
    status: state.status,
    progress: state.progress,
    handleStartWorking,
    handleFinishWorking,
    handleReset,
  };
}
